import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Grid, Chip, Typography } from '@mui/material';
import { Button, Form } from 'antd';
import { useAuthHeader, useAuthUser } from '../../../../../Services/Auth/sessionAuth';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import Params from '../../../../../Types/ParamsPicklist';
import { Crypter } from '../../../../../Functions/Security/Crypter';
import Datas from '../../../../../Data/Data.json';
import InputForm from '../../../../InputT';
import SearchModal from '../../../../SearchModal';
import Utilisateur from '../../../../../Models/Utilisateur';
import SelectOption from '../../../../SelectOption';
import ReformatDate from '../../../../../Functions/Traitement/ReformatDate';
import { SwipeUp } from '@mui/icons-material';
import SearchInput from '../../../../SearchInput';

// ==================== PARAMÈTRES DE RECHERCHE ====================
let searchFinForm: Params = { CodeAgence: '', Table: "DEMAND_FINIJARA", Colonne1: "NumeroDemande", Colonne2: "NOMCLI", Colagence: '101', Choix: 22 };
let searchSimul: Params = { CodeAgence: '', Table: "SimulationIjara", Colonne1: "CodeSimul", Colonne2: "Nomcli", Colagence: '101', Choix: 22 };
let searchParamsIjara: Params = { CodeAgence: '', Table: "ParamsIjara", Colonne1: "CodeIjara", Colonne2: "LibelleIjara", Colagence: '101', Choix: 22 };
let searchIndice: Params = { CodeAgence: '', Table: "CLIENT", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: '101', Choix: 22 };
let searchPeriod: Params = { CodeAgence: '', Table: "v_period_lib", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: '101', Choix: 22 };
let searchObjet: Params = { CodeAgence: 'OBJETFIN', Table: "LIB", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: 'RUBRIQ', Choix: 22 };
let searchMotif: Params = { CodeAgence: 'MOTIFPRET', Table: "LIB", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: 'RUBRIQ', Choix: 22 };
let searchActivite: Params = { CodeAgence: '', Table: "TABNAE", Colonne1: "CODNAE", Colonne2: "LIBELL", Colagence: '101', Choix: 22 };

// ==================== INTERFACE DES PROPS ====================
interface Tab1Props {
  form?: any;
  disabled?: boolean;
  LoadGarantie: Function;
  onEnableEdit?: () => void;
  isLoading?: boolean;
}

// ==================== FONCTION DE STYLE DU STATUT ====================
/**
 * Retourne la couleur, le fond et le libellé du statut à afficher.
 * On se base prioritairement sur StatutDemande ; si absent, on retombe sur EtapeActuelle.
 */
const getStatutStyle = (etape?: string, statutDemande?: string) => {
  const statutUpper = (statutDemande || '').toUpperCase();
  const etapeUpper = (etape || '').toUpperCase();

  switch (statutUpper) {
    case 'EN_ATTENTE':
      return { color: '#faad14', bg: '#fffbe6', label: 'En attente' };
    case 'VALIDEE':
    case 'VALIDÉE':
      return { color: '#52c41a', bg: '#f6ffed', label: 'Validée' };
    case 'REJET':
    case 'REJETEE':
    case 'REJETÉE':
      return { color: '#f5222d', bg: '#fff1f0', label: 'Rejetée' };
    case 'EN_ANALYSE':
      return { color: '#1890ff', bg: '#e6f7ff', label: 'En analyse' };
    case 'BROUILLON':
      return { color: '#8c8c8c', bg: '#f5f5f5', label: 'Brouillon' };
    case 'ANNULE':
    case 'ANNULÉE':
      return { color: '#8c8c8c', bg: '#f5f5f5', label: 'Annulée' };
  }

  // Fallback : si StatutDemande est vide mais qu'on a un EtapeActuelle
  if (etapeUpper) {
    return { color: '#8c8c8c', bg: '#f5f5f5', label: etapeUpper };
  }

  return { color: '#8c8c8c', bg: '#f5f5f5', label: 'Non défini' };
};

// ==================== COMPOSANT ====================
function Tab1({ form, disabled, LoadGarantie, onEnableEdit, isLoading }: Tab1Props) {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const [loading, setLoading] = useState(false);
  const authHeader = useAuthHeader();
  const isSimulationLoadRef = useRef(false);

  // ==================== OBSERVATEURS ====================
  const numcliValue = Form.useWatch('NUMCLI', form);
  const codeIjaraValue = Form.useWatch('CodeIjara', form);
  // ✅ useWatch fonctionne avec les Form.Item enregistrés plus bas
  const EtapeActuelle = Form.useWatch('EtapeActuelle', form);
  const StatutDemande = Form.useWatch('StatutDemande', form);

  // ==================== PARAMÈTRES DYNAMIQUES ====================
  const searchNumCompte = useMemo<Params>(() => {
    return {
      CodeAgence: numcliValue,
      Table: "ViewAnyIjaraCptRemboursement",
      Colonne1: "NUMCPT",
      Colonne2: "NOMCPT",
      Colagence: "NUMCLI",
      colfiltre: "CodeIjara",
      valfiltre: codeIjaraValue,
      Choix: 22
    };
  }, [codeIjaraValue, numcliValue]);

  const searchFournisseur = useMemo<Params>(() => {
    return {
      CodeAgence: codeIjaraValue,
      Table: "ViewAnyIjaraCptFournisseur",
      Colonne1: "NUMCPT",
      Colonne2: "NOMCPT",
      Colagence: "CodeIjara",
      Choix: 22
    };
  }, [codeIjaraValue]);

  const searchActif = useMemo<Params>(() => {
    return {
      CodeAgence: '',
      Table: "ViewAnyActifIjara",
      Colonne1: "CodeActif",
      Colonne2: "Designation",
      Colagence: '',
      colfiltre: "CodeIjara",
      valfiltre: codeIjaraValue,
      Choix: 22
    };
  }, [codeIjaraValue]);

  // Condition de désactivation (identique à Salam)
  const isCptReglementDisabled = disabled || !numcliValue || !codeIjaraValue;

  // Les autres champs ne sont sélectionnables qu'après le choix du Code Ijara
  const fieldsDisabled = !codeIjaraValue;

  const prevCodeIjaraRef = useRef<string | undefined>(codeIjaraValue);

  // Effet pour vider les comptes lors du changement de CodeIjara
  useEffect(() => {
    if (disabled || isSimulationLoadRef.current || isLoading) {
      prevCodeIjaraRef.current = codeIjaraValue;
      return;
    }
    if (prevCodeIjaraRef.current === codeIjaraValue) {
      return;
    }
    form.setFieldsValue({
      CPTREMBOURSEMENT: undefined,
      CPTREGLEMENT: undefined,
    });
    console.log("🧹 CodeIjara changé, comptes vidés");
    prevCodeIjaraRef.current = codeIjaraValue;
  }, [codeIjaraValue, form, disabled, isLoading]);

  // ==================== CONSULTATION TYPE IJARAH (FRAIS) ====================
  function consultTypeIjarah() {
    const code = Crypter.encrypt((form.getFieldValue("CodeIjara")));
    if (!code) {
      SweetAlert.fire({ title: "Svp renseigner le Code du type de Ijara ", icon: "warning" });
      return;
    }
    setLoading(true);
    let config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: Datas.ApiUrl.ReadParamsIjarah,
      headers: {
        'CypherCodeIjara': code,
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      },
    };
    axios.request(config)
      .then((response) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200) {
            const params = res.Result.Table1?.[0] || {};
            const isGarantieOblig = ['true','1','yes','oui', true].some(v =>
              String(params?.IsGarantieObligatoir ?? '').toLowerCase() === String(v)
            );
            form.setFieldValue('IsGarantieObligatoir', isGarantieOblig);
            const frais = params.MontantFrais || 0;
            form.setFieldValue("MontantFrais", frais);
            form.setFieldValue("CodeFrais", res.Result.Table5?.map((x: any) => x.CODOPE) || []);
          } else {
            SweetAlert.fire({ title: "Warning", text: res.Message, icon: "warning" });
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        SweetAlert.fire({ title: "Error", text: error, icon: "error" });
      });
  }

  // ==================== CHANGEMENT DE CLIENT ====================
  function handleClientChange() {
    form.setFieldValue("CPTREMBOURSEMENT", '');
    form.setFieldValue("CPTREGLEMENT", '');
  }

  // ==================== CHARGEMENT D'UNE SIMULATION ====================
  function load() {
    const codeSimul = form.getFieldValue("CodeSimul");
    if (!codeSimul) {
      SweetAlert.fire({ title: "Veuillez sélectionner une simulation", icon: "warning" });
      return;
    }
    setLoading(true);
    isSimulationLoadRef.current = true;

    let data = Crypter.encrypt(codeSimul);
    let config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: Datas.ApiUrl.ReadSimulationIjarah,
      headers: {
        'CypherCode': data,
        'Authorization': authHeader(),
        'Content-Type': 'application/json'
      },
    };
    axios.request(config)
      .then((response) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200) {
            const sim = res.Result.Table2[0];
            form.setFieldsValue({
              ...sim,
              CodeSimul: sim.CodeSimul,
              CodeIjara: sim.CodeIjara,
              lib_CodeIjara: sim.lib_CodeIjara ?? sim.LibelleIjara,
              TypeIjaraDemande: sim.TypeIjaraDemande ?? sim.TypeIjara,
              NUMCLI: sim.Numcli,
              NOMCLI: sim.Nomcli,
              lib_NUMCLI: sim.lib_NUMCLI ?? sim.Nomcli,
              CODOBJECT: sim.CODOBJECT,
              lib_CODOBJECT: sim.lib_CODOBJECT ?? sim.LibelleObjet,
              MOTIFDEM: sim.MOTIFDEM,
              lib_MOTIFDEM: sim.lib_MOTIFDEM ?? sim.LibelleMotif,
              CODACTIVE: sim.CODACTIVE,
              lib_CODACTIVE: sim.lib_CODACTIVE ?? sim.LibelleActivite,
              CodeActif: sim.CodeActif,
              lib_CodeActif: sim.lib_CodeActif ?? sim.Designation,
              CoutAcquisitionDemande: sim.CoutAcquisitionBien,
              MargeLocativeDemande: sim.MargeLocative,
              ValeurResiduelleDemande: sim.ValeurResiduelle,
              DureeContrat: sim.DureeContratMois,
              DureeDifferee: sim.DureeDiffereMois ?? sim.DureeDifferee,
              DureeAmortBien: sim.DureeAmortBienMois ?? sim.DureeAmortBien,
              NombreEch: sim.NombreEch,
              NombreEchAmort: sim.NombreEchAmort,
              Periodicite: sim.Periodicite,
              lib_Periodicite: sim.lib_Periodicite ?? sim.LibellePeriodicite,
              PeriodiciteAmort: sim.PeriodiciteAmort,
              lib_PeriodiciteAmort: sim.lib_PeriodiciteAmort ?? sim.LibellePeriodiciteAmort,
              DatePremEch: sim.DatePremEch ? ReformatDate(sim.DatePremEch) : undefined,
              DatePremEchAmort: sim.DatePremEchAmort ? ReformatDate(sim.DatePremEchAmort) : undefined,
              MontantCharge: sim.MontantCharge,
              Quantite: sim.Quantite,
              CommentaireDemande: sim.CommentaireDemande,
              CPTREMBOURSEMENT: '',
              CPTREGLEMENT: '',
            });

            // 🔥 Activer le mode édition (déverrouiller le formulaire)
            if (onEnableEdit) onEnableEdit();

            // Executeafter : mêmes traitements qu'une sélection manuelle des champs remplis
            handleClientChange();
            LoadGarantie();
            consultTypeIjarah();
          } else {
            SweetAlert.fire({ title: res.Message, icon: "warning" });
          }
        }
        setLoading(false);
        setTimeout(() => {
          isSimulationLoadRef.current = false;
        }, 0);
      })
      .catch((error) => {
        setLoading(false);
        isSimulationLoadRef.current = false;
        console.log(error);
        SweetAlert.fire({ title: error, icon: "error" });
      });
  }

  // ==================== STYLE DU STATUT ====================
  const statutStyle = getStatutStyle(EtapeActuelle, StatutDemande);

  // ==================== RENDU ====================
  return (
    <div className='tabItem'>
      <div>
        {/* Ligne de simulation */}
        <Box fontStyle={"inherit"} display={"flex"} mb={2} sx={{ width: '80%' }}>
          <Box flex={1}>
            <SearchModal notRequired width={5} form={form} Titre='Recherche de simulation Ijarah' label='Code Simulation' name='CodeSimul' Parametre={searchSimul} />
          </Box>
          <Button onClick={load} style={{ top: '10px', color: 'white' }} className='bt_add' type="ghost">
            {loading ? <CircularProgress size={"20px"} /> : "Load"}
          </Button>
        </Box>

        <Grid container className='border' sx={{ width: "85%", position: "absolute" }}>

          {/* ✅ CHAMPS CACHÉS : enregistre EtapeActuelle et StatutDemande dans le formulaire pour Form.useWatch */}
          <Form.Item name="EtapeActuelle" noStyle>
            <input type="hidden" />
          </Form.Item>
          <Form.Item name="StatutDemande" noStyle>
            <input type="hidden" />
          </Form.Item>

          {/* ===== BANDEAU DE STATUT (visible en consultation) ===== */}
          {disabled && (EtapeActuelle || StatutDemande) && (
            <Grid item xs={12} md={12} lg={12} sx={{ p: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  bgcolor: statutStyle.bg,
                  borderBottom: `2px solid ${statutStyle.color}`,
                  borderRadius: '4px 4px 0 0',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#333' }}>
                  Statut de la demande :
                </Typography>
                <Chip
                  label={statutStyle.label}
                  sx={{
                    bgcolor: statutStyle.color,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 12,
                    height: 26,
                  }}
                />
                <Typography sx={{ ml: 'auto', fontSize: 12, color: '#666' }}>
                  Étape code : <b>{EtapeActuelle || 'N/A'}</b>
                </Typography>
              </Box>
            </Grid>
          )}

          {/* ======= COLONNE GAUCHE ======= */}
          <Grid item xs={12} md={6} lg={6} className='border-left' style={{ padding: '8px' }}>
            <SearchInput
              width={5}
              form={form}
              Titre='Recherche Numéro de demande Ijara'
              label='Numéro Demande'
              name='NumeroDemande'
              Parametre={searchFinForm}
              dissabledCode={!disabled}
              notRequired
            />
            <SearchModal
              ExecuteafterSelect1={LoadGarantie}
              ExecuteafterSelect2={handleClientChange}
              width={5}
              form={form}
              Titre="Recherche Indice client"
              label='Client'
              name='NUMCLI'
              Parametre={searchIndice}
              disabled={fieldsDisabled}
            />
            <SelectOption
              key={`cptRemb-${numcliValue}-${codeIjaraValue}`}
              width={5}
              label="Compte remboursement"
              name="CPTREMBOURSEMENT"
              Parametre={searchNumCompte}
              disabled={isCptReglementDisabled}
            />
            <SearchModal
              width={5}
              form={form}
              Titre="Objet du financement"
              label='Objet'
              name='CODOBJECT'
              Parametre={searchObjet}
              disabled={fieldsDisabled}
            />
            <SearchModal
              width={5}
              form={form}
              Titre="Motif"
              label='Motif'
              name='MOTIFDEM'
              Parametre={searchMotif}
              disabled={fieldsDisabled}
            />
            <SearchModal
              width={5}
              form={form}
              Titre="Activité du client"
              label='Activité'
              name='CODACTIVE'
              Parametre={searchActivite}
              disabled={fieldsDisabled}
            />
            <InputForm
              notRequired
              width={5}
              disabled
              money
              label='Frais de service'
              name='MontantFrais'
              type='number'
            />
            <InputForm
              notRequired
              width={5}
              money
              label='Montant Charge'
              name='MontantCharge'
              type='number'
              disabled={fieldsDisabled}
            />
            <InputForm
              notRequired
              width={5}
              money
              label='MontantPromesse'
              name='MontantPromesse'
              type='number'
              disabled={fieldsDisabled}
            />
            <SearchModal
              width={5}
              form={form}
              Titre="Périodicité"
              label='Periodicite'
              name='Periodicite'
              Parametre={searchPeriod}
              disabled={fieldsDisabled}
            />
            <SearchModal
              width={5}
              form={form}
              Titre="Périodicité Amortissement"
              label='Periodicite Amortissement'
              name='PeriodiciteAmort'
              Parametre={searchPeriod}
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              money
              label='Nombre Échéance'
              icon={<SwipeUp color='disabled' fontSize='small' />}
              name='NombreEch'
              type='number'
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              money
              label='Nombre Échéance Amortissement'
              icon={<SwipeUp color='disabled' fontSize='small' />}
              name='NombreEchAmort'
              type='number'
              disabled={fieldsDisabled}
            />
          </Grid>

          {/* ======= COLONNE DROITE ======= */}
          <Grid item xs={12} md={6} lg={6} className='border-left' style={{ padding: '8px' }}>
            <SearchModal
              handleFunction={() => { }}
              ExecuteafterSelect1={consultTypeIjarah}
              width={5}
              form={form}
              Titre="Type de Ijara"
              label='Code Ijara'
              name='CodeIjara'
              Parametre={searchParamsIjara}
            />
            <InputForm
              width={5}
              money
              label="Coût d'acquisition"
              name='CoutAcquisitionDemande'
              type='number'
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              money
              label="Marge locative"
              name='MargeLocativeDemande'
              type='number'
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              label="Date 1ère échéance"
              name='DatePremEch'
              type='date'
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              label="Date 1ère échéance Amortissement"
              name='DatePremEchAmort'
              type='date'
              disabled={fieldsDisabled}
            />
            <SearchModal
              width={5}
              form={form}
              Titre="Actif"
              label='Actif'
              name='CodeActif'
              Parametre={searchActif}
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              money
              label="Valeur résiduelle"
              name='ValeurResiduelleDemande'
              type='number'
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              money
              label="Durée contrat (mois)"
              name='DureeContrat'
              type='number'
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              money
              label="Durée différée (mois)"
              name='DureeDifferee'
              type='number'
              disabled={fieldsDisabled}
            />
            <InputForm
              width={5}
              money
              label="Durée amortissement"
              name='DureeAmortBien'
              type='number'
              disabled={fieldsDisabled}
            />
          </Grid>

          {/* ======= COMMENTAIRE ======= */}
          <Grid item xs={12} md={12} lg={12} className='border-left' style={{ padding: '8px' }}>
            <InputForm
              width={4}
              label='Commentaire'
              name='CommentaireDemande'
              type='TextArea'
              disabled={fieldsDisabled}
            />
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

export default Tab1;